import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  sendOutsService,
  CreateSendOutInput,
  LogReceiptInput,
  FollowUpInput,
  ShipSendOutInput,
  SendOut,
  SendOutFollowUp,
  SendOutWithCaseDetails,
  ExternalInstitution,
  QueueSummary,
  NeedingAttentionResult,
} from "../services/sendOuts.service";

// ==================== QUERY KEYS ====================

export const sendOutKeys = {
  all: ["send-outs"] as const,
  byCase: (caseId: string) => [...sendOutKeys.all, "case", caseId] as const,
  institutions: () => [...sendOutKeys.all, "institutions"] as const,
  detail: (id: string) => [...sendOutKeys.all, "detail", id] as const,
  pendingApproval: () => [...sendOutKeys.all, "pending-approval"] as const,
  pendingShipping: () => [...sendOutKeys.all, "pending-shipping"] as const,
  tissueCypher: () => [...sendOutKeys.all, "tissue-cypher"] as const,
  inTransit: () => [...sendOutKeys.all, "in-transit"] as const,
  overdue: () => [...sendOutKeys.all, "overdue"] as const,
  rejected: () => [...sendOutKeys.all, "rejected"] as const,
  returned: () => [...sendOutKeys.all, "returned"] as const,
  sent: () => [...sendOutKeys.all, "sent"] as const,
  needingAttention: () => [...sendOutKeys.all, "needing-attention"] as const,
  summary: () => [...sendOutKeys.all, "summary"] as const,
  followUps: (id: string) => [...sendOutKeys.all, "follow-ups", id] as const,
  caseDetails: (id: string) =>
    [...sendOutKeys.all, "case-details", id] as const,
};

// ==================== QUERIES ====================

/**
 * Hook to get send-outs for a specific case
 */
export function useCaseSendOuts(caseId: string | undefined) {
  return useQuery<SendOut[]>({
    queryKey: sendOutKeys.byCase(caseId!),
    queryFn: () => sendOutsService.getByCaseId(caseId!),
    enabled: !!caseId,
  });
}

/**
 * Hook to get active external institutions for dropdown selection
 */
export function useExternalInstitutions() {
  return useQuery<ExternalInstitution[]>({
    queryKey: sendOutKeys.institutions(),
    queryFn: () => sendOutsService.getInstitutions(),
    staleTime: 300000, // Cache for 5 minutes (institutions don't change often)
  });
}

/**
 * Hook to get send-outs pending approval
 */
export function usePendingApproval() {
  return useQuery<SendOut[]>({
    queryKey: sendOutKeys.pendingApproval(),
    queryFn: () => sendOutsService.getPendingApproval(),
    refetchInterval: 30000, // 30s polling
  });
}

/**
 * Hook to get send-outs pending shipping (for Shipping Queue)
 * Per C-802 §8.1, approved send-outs appear in Shipping Queue
 */
export function usePendingShippingSendOuts() {
  return useQuery<SendOut[]>({
    queryKey: sendOutKeys.pendingShipping(),
    queryFn: () => sendOutsService.getPendingShipping(),
    refetchInterval: 30000,
  });
}

export function useTissueCypherSendOuts() {
  return useQuery<SendOut[]>({
    queryKey: sendOutKeys.tissueCypher(),
    queryFn: () => sendOutsService.getTissueCypher(),
    refetchInterval: 30000,
  });
}

/**
 * Hook to get in-transit send-outs (shipped but not received)
 */
export function useInTransitSendOuts() {
  return useQuery<SendOut[]>({
    queryKey: sendOutKeys.inTransit(),
    queryFn: () => sendOutsService.getInTransit(),
    refetchInterval: 30000,
  });
}

/**
 * Hook to get overdue send-outs
 */
export function useOverdueSendOuts() {
  return useQuery<SendOut[]>({
    queryKey: sendOutKeys.overdue(),
    queryFn: () => sendOutsService.getOverdue(),
    refetchInterval: 30000,
  });
}

/**
 * Hook to get rejected send-outs for audit trail (C-2497)
 */
export function useRejectedSendOuts() {
  return useQuery<SendOut[]>({
    queryKey: sendOutKeys.rejected(),
    queryFn: () => sendOutsService.getRejected(),
  });
}

/**
 * Hook to get queue summary counts for badges
 */
export function useQueueSummary() {
  return useQuery<QueueSummary>({
    queryKey: sendOutKeys.summary(),
    queryFn: () => sendOutsService.getQueueSummary(),
    refetchInterval: 30000,
  });
}

/**
 * Hook to get a single send-out with full details
 */
export function useSendOutDetail(id: string | undefined) {
  return useQuery<SendOut>({
    queryKey: sendOutKeys.detail(id!),
    queryFn: () => sendOutsService.getByIdWithDetails(id!),
    enabled: !!id,
  });
}

/**
 * Hook to get a send-out with enriched case details and resolved materials
 */
export function useSendOutWithCaseDetails(id: string | undefined) {
  return useQuery<SendOutWithCaseDetails>({
    queryKey: sendOutKeys.caseDetails(id!),
    queryFn: () => sendOutsService.getByIdWithCaseDetails(id!),
    enabled: !!id,
  });
}

/**
 * Hook to get follow-ups for a send-out
 */
export function useSendOutFollowUps(id: string | undefined) {
  return useQuery<SendOutFollowUp[]>({
    queryKey: sendOutKeys.followUps(id!),
    queryFn: () => sendOutsService.getFollowUps(id!),
    enabled: !!id,
  });
}

// ==================== MUTATIONS ====================

/**
 * Invalidate all send-out queue queries
 */
function invalidateQueueQueries(
  queryClient: ReturnType<typeof useQueryClient>
) {
  queryClient.invalidateQueries({ queryKey: sendOutKeys.all });
}

/**
 * Hook to create a new send-out request
 */
export function useCreateSendOut() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSendOutInput) => sendOutsService.create(data),
    onSuccess: (_, variables) => {
      // Invalidate send-outs for this case
      queryClient.invalidateQueries({
        queryKey: sendOutKeys.byCase(variables.caseId),
      });
      // Also invalidate the case data to reflect any status changes
      queryClient.invalidateQueries({
        queryKey: ["case-for-pathologist-review", variables.caseId],
      });
      // Invalidate queue data
      invalidateQueueQueries(queryClient);
    },
  });
}

/**
 * Hook to approve a send-out
 */
export function useApproveSendOut() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => sendOutsService.approve(id),
    onSuccess: () => {
      invalidateQueueQueries(queryClient);
    },
  });
}

/**
 * Hook to reject a send-out
 */
export function useRejectSendOut() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      sendOutsService.reject(id, reason),
    onSuccess: () => {
      invalidateQueueQueries(queryClient);
    },
  });
}

/**
 * Hook to mark a send-out as shipped
 */
export function useMarkShipped() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: ShipSendOutInput }) =>
      sendOutsService.markShipped(id, data),
    onSuccess: () => {
      invalidateQueueQueries(queryClient);
    },
  });
}

/**
 * Hook to log receipt of a send-out (full receipt)
 */
export function useLogReceipt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
      isPartial,
    }: {
      id: string;
      data: LogReceiptInput;
      isPartial: boolean;
    }) =>
      isPartial
        ? sendOutsService.markPartiallyReceived(id, data)
        : sendOutsService.markReceived(id, data),
    onSuccess: (_, variables) => {
      // Invalidate the specific send-out detail queries
      queryClient.invalidateQueries({
        queryKey: sendOutKeys.detail(variables.id),
      });
      queryClient.invalidateQueries({
        queryKey: sendOutKeys.caseDetails(variables.id),
      });
      // Invalidate all queue queries
      invalidateQueueQueries(queryClient);
    },
  });
}

/**
 * Hook to add a follow-up entry
 */
export function useAddFollowUp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: FollowUpInput }) =>
      sendOutsService.addFollowUp(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: sendOutKeys.followUps(variables.id),
      });
      queryClient.invalidateQueries({
        queryKey: sendOutKeys.detail(variables.id),
      });
      queryClient.invalidateQueries({
        queryKey: sendOutKeys.caseDetails(variables.id),
      });
    },
  });
}

/**
 * Hook to get returned send-outs
 */
export function useReturnedSendOuts() {
  return useQuery<SendOut[]>({
    queryKey: sendOutKeys.returned(),
    queryFn: () => sendOutsService.getReturned(),
    refetchInterval: 30000,
  });
}

/**
 * Hook to get non-overdue shipped send-outs (Sent tab)
 */
export function useSentSendOuts() {
  return useQuery<SendOut[]>({
    queryKey: sendOutKeys.sent(),
    queryFn: () => sendOutsService.getSent(),
    refetchInterval: 30000,
  });
}

/**
 * Hook to get send-outs needing attention (dashboard widget)
 */
export function useNeedingAttention() {
  return useQuery<NeedingAttentionResult>({
    queryKey: sendOutKeys.needingAttention(),
    queryFn: () => sendOutsService.getNeedingAttention(),
    refetchInterval: 60000,
  });
}

/**
 * Hook to resubmit a rejected send-out
 */
export function useResubmitSendOut() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateSendOutInput }) =>
      sendOutsService.resubmitSendOut(id, data),
    onSuccess: () => {
      invalidateQueueQueries(queryClient);
    },
  });
}

/**
 * Hook to mark a send-out as reviewed (clears isPendingReview)
 */
export function useMarkSendOutReviewed() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => sendOutsService.markSendOutReviewed(id),
    onSuccess: () => {
      invalidateQueueQueries(queryClient);
    },
  });
}

/**
 * Hook to dismiss an overdue alert for 7 days (C-804 §8.2)
 */
export function useDismissAlert() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => sendOutsService.dismissAlert(id),
    onSuccess: () => {
      // Invalidate overdue list and summary since item will be hidden
      invalidateQueueQueries(queryClient);
    },
  });
}
